<template>
  <div class="w-full">
    <FormField name="locations">
      <FormItem>
        <FormLabel class="flex items-center">
          <MapPin class="text-sm h-4 w-4 mr-2" /> Location(s)
        </FormLabel>
        <FormControl>
          <!-- Selected Locations -->
          <div class="flex flex-wrap gap-2 mb-2">
            <div
              v-for="location in modelValue"
              :key="location.id"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 border border-gray-200"
            >
              <span class="text-sm">{{ location.location_name }}</span>
              <X
                class="h-3 w-3 cursor-pointer hover:text-red-500"
                @click="removeLocation(location)"
              />
            </div>
          </div>

          <!-- Search Input -->
          <div class="relative" ref="searchContainer">
            <div class="relative">
              <Search
                v-if="!isLoading"
                class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
              />
              <Loader2
                v-else
                class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin"
              />
              <Input
                v-model="searchQuery"
                type="text"
                placeholder="Search locations (e.g. 'Remote US' or 'San Francisco')"
                class="pl-10"
                @focus="handleFocus"
                @blur="handleBlur"
              />
            </div>

            <!-- Search Results Dropdown -->
            <div
              v-if="showDropdown && (searchResults.length > 0 || isLoading)"
              class="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-[300px] overflow-y-auto"
            >
              <div v-if="isLoading" class="p-4 text-center">
                <Loader2 class="h-5 w-5 animate-spin mx-auto" />
                <span class="text-sm text-gray-500">Searching locations...</span>
              </div>
              
              <div v-else>
                <div
                  v-for="result in searchResults"
                  :key="result.id"
                  class="p-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                  @click="handleLocationSelect(result)"
                >
                  <div class="flex items-center gap-2">
                    <span v-if="result.type === 'existing'" class="text-green-500">
                      <CheckCircle2 class="h-4 w-4" />
                    </span>
                    <span v-else class="text-blue-500">
                      <PlusCircle class="h-4 w-4" />
                    </span>
                    <span class="text-sm">{{ result.label }}</span>
                  </div>
                  <StatusBadge
                    :variant="result.type === 'existing' ? 'secondary' : 'outline'"
                    class="text-xs"
                  >
                    {{ result.type === 'existing' ? 'Existing' : 'New' }}
                  </StatusBadge>
                </div>
              </div>
            </div>
          </div>
        </FormControl>
      </FormItem>
    </FormField>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import {
  MapPin,
  X,
  Search,
  PlusCircle,
  Loader2,
  CheckCircle2
} from 'lucide-vue-next'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { StatusBadge } from '@/components/ui/badge/index'
import { useDebounce } from '@/composables/useDebounce'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { AllowedLocation } from '@/types/locations'

interface LocationResult {
  id: string
  type: 'existing' | 'suggestion'
  label: string
  location_id?: number
  create_data?: {
    location_type: string
    country_id?: number
    city_id?: number
    region?: string
    us_state?: string
  }
  match_score: number
}

const props = defineProps<{
  modelValue: AllowedLocation[]
}>()

const emit = defineEmits(['update:modelValue'])

// State
const searchQuery = ref('')
const searchResults = ref<LocationResult[]>([])
const isLoading = ref(false)
const showDropdown = ref(false)
const searchContainer = ref<HTMLElement | null>(null)

// Quick location options
const quickLocations = [
  {
    id: 'global_remote',
    label: '🌍 Remote (Global)',
    create_data: {
      location_type: 'remote_global'
    }
  },
  {
    id: 'us_remote',
    label: '🏠 Remote (US)',
    create_data: {
      location_type: 'remote_country',
      country_id: 230 // Assuming 230 is US ID in your system
    }
  },
  {
    id: 'europe_remote',
    label: '🏠 Remote (Europe)',
    create_data: {
      location_type: 'remote_region',
      region: 'EUROPE'
    }
  }
]

// Search handling
const debouncedSearch = useDebounce(async (query: string) => {
  if (query.length < 2) {
    searchResults.value = []
    return
  }

  isLoading.value = true
  try {
    const response = await api.get(API_ROUTES.SMART_SEARCH_LOCATIONS, {
      params: { q: query }
    })
    searchResults.value = response.data.results
  } catch (error) {
    console.error('Location search failed:', error)
    searchResults.value = []
  } finally {
    isLoading.value = false
  }
}, 500)

watch(searchQuery, (newQuery) => {
  if (newQuery) {
    debouncedSearch(newQuery)
  } else {
    searchResults.value = []
    showDropdown.value = false
  }
})

// Show dropdown when search results are received
watch(searchResults, (newResults) => {
  if (newResults.length > 0) {
    showDropdown.value = true
  }
})

// Location selection handling
async function handleLocationSelect(result: LocationResult) {
  try {
    let locationToAdd: AllowedLocation

    if (result.type === 'existing') {
      locationToAdd = {
        id: result.location_id!,
        city: null,
        city_details: null,
        country: null,
        country_details: null,
        location_type: result.create_data?.location_type as any,
        region: null,
        us_state: null,
        location_name: result.label
      }
    } else {
      // Create new location
      const response = await api.post(API_ROUTES.CREATE_JOB_LOCATION, result.create_data)
      locationToAdd = response.data
    }

    // Add to selected locations if not already present
    if (!props.modelValue.some(loc => loc.id === locationToAdd.id)) {
      emit('update:modelValue', [...props.modelValue, locationToAdd])
    }

    // Clear search
    searchQuery.value = ''
    showDropdown.value = false
  } catch (error) {
    console.error('Failed to add location:', error)
    // You might want to show an error toast here
  }
}


function removeLocation(location: AllowedLocation) {
  emit('update:modelValue', props.modelValue.filter(loc => loc.id !== location.id))
}

// UI handling
function handleFocus() {
  if (searchQuery.value) {
    showDropdown.value = true
  }
}

function handleBlur() {
  // Delay hiding dropdown to allow click events to fire
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

// Click outside handling
function handleClickOutside(event: MouseEvent) {
  if (searchContainer.value && !searchContainer.value.contains(event.target as Node)) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script> 